/**
 * Transcription API Client
 *
 * Handles audio transcription requests to the backend.
 * Uses cookie auth (same-origin, credentials: include).
 */

import { getApiBase } from './fetch';

export interface TranscriptionResult {
  text: string;
  duration?: number;
  language?: string;
}

export interface TranscriptionOptions {
  language?: string;
  prompt?: string;
}

/** Map MIME types to file extensions for the multipart upload */
const MIME_TO_EXT: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/webm;codecs=opus': 'webm',
  'audio/mp4': 'm4a',
  'audio/ogg': 'ogg',
  'audio/ogg;codecs=opus': 'ogg',
  'audio/wav': 'wav',
};

/**
 * Transcribe audio file using the backend API
 */
export async function transcribeAudio(
  audioBlob: Blob,
  options?: TranscriptionOptions,
): Promise<TranscriptionResult> {
  const apiBase = getApiBase();

  // Determine file extension from actual blob MIME type
  const ext = MIME_TO_EXT[audioBlob.type] || 'webm';

  const formData = new FormData();
  formData.append('audio', audioBlob, `recording.${ext}`);

  if (options?.language) {
    formData.append('language', options.language);
  }

  if (options?.prompt) {
    formData.append('prompt', options.prompt);
  }

  const response = await fetch(`${apiBase}/transcription`, {
    method: 'POST',
    credentials: 'include', // Send session cookies for auth
    body: formData,
    // Don't set Content-Type — browser sets multipart boundary automatically
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || `HTTP ${response.status}`;
    throw new Error(message);
  }

  return response.json();
}
